import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { Drone, droneLabel } from '../../types/drone';
import { useNow } from '../../hooks/useNow';
import { credentialsAttention, credentialsAttentionLabel, renewCommands } from '../../utils/credentials';

/**
 * Across the fleet view while any drone's credentials expire within 7 days or
 * no longer work (#777, C16). Each drone is named with the command that renews
 * it, so the pilot does not have to open every card to find out which.
 */
export const CredentialsBanner: React.FC<{ drones: Drone[] }> = ({ drones }) => {
  const now = useNow(60_000);
  const needing = drones
    .map((drone) => ({ drone, attention: credentialsAttention(drone, now) }))
    .filter((d) => d.attention !== null);
  if (needing.length === 0) return null;

  const lapsed = needing.some((d) => d.attention !== 'expiring');
  return (
    <Alert severity={lapsed ? 'error' : 'warning'} role="region" aria-label="Drone credentials">
      <AlertTitle>
        {needing.length === 1
          ? '1 drone needs new credentials'
          : `${needing.length} drones need new credentials`}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        A drone whose credentials lapse cannot reach the fleet. Renew it from a terminal that can
        reach it.
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {needing.map(({ drone, attention }) => (
          <li key={drone.id}>
            <Typography variant="body2">
              {droneLabel(drone)}: {credentialsAttentionLabel(drone, attention!, now).toLowerCase()} —{' '}
              <Box component="code" sx={{ overflowWrap: 'anywhere' }}>
                {renewCommands(drone)[0]}
              </Box>
            </Typography>
          </li>
        ))}
      </Box>
    </Alert>
  );
};
